import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LuBell, LuShare2, LuClipboardCheck, LuRefreshCw, LuCheck, LuInbox, LuBuilding2, LuDownload, LuAlertTriangle, LuX, LuBellRing, LuClock } from 'react-icons/lu';
import { getNotifications, getUnreadNotificationsCount, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification } from '../api/routes/notification';
import { timeAgo } from '../utils/fileTypeIcons';
import { playNotificationSound } from '../utils/notificationSound';
import { updateFaviconBadge } from '../utils/faviconBadge';
import { pushSupporte, demanderEtSabonner, estIOSSafariHorsAccueil } from '../utils/pushNotifications';
import echo from '../utils/echo';
import SwipeToDelete from './SwipeToDelete';

const TYPE_VISUAL = {
    partage: { icon: LuShare2, tint: 'bg-primary/10 text-primary' },
    a_valider: { icon: LuClipboardCheck, tint: 'bg-accent/20 text-accent-foreground' },
    archive_info: { icon: LuBellRing, tint: 'bg-primary/10 text-primary' },
    pai_en_retard: { icon: LuAlertTriangle, tint: 'bg-destructive/10 text-destructive' },
    pai_rappel: { icon: LuClock, tint: 'bg-amber-500/10 text-amber-600' },
    pai_escalade: { icon: LuBellRing, tint: 'bg-destructive/10 text-destructive' },
    statut: { icon: LuRefreshCw, tint: 'bg-green-500/10 text-green-600' },
    transmission_service: { icon: LuBuilding2, tint: 'bg-accent/20 text-accent-foreground' },
    export_pret: { icon: LuDownload, tint: 'bg-green-500/10 text-green-600' },
    export_echoue: { icon: LuAlertTriangle, tint: 'bg-destructive/10 text-destructive' },
};

const POLL_INTERVAL_MS = 30000;

/**
 * Contenu du popup affiché à l'arrivée d'une notification en temps réel —
 * repris de la même charte (icône teintée + titre + message) que le panneau
 * de la cloche, pour que la notification soit reconnaissable qu'on la voie
 * au moment où elle arrive ou plus tard dans l'historique. Cliquable pour
 * aller directement au document concerné.
 */
function ContenuToastNotification({ notification, onOuvrir, closeToast }) {
    const visual = TYPE_VISUAL[notification.type] || TYPE_VISUAL.statut;
    const Icon = visual.icon;
    return (
        <div
            onClick={() => { onOuvrir(); closeToast(); }}
            className='flex items-start gap-3 pr-1 cursor-pointer'
        >
            <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${visual.tint}`}>
                <Icon size={16} />
            </div>
            <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-foreground truncate'>{notification.titre}</p>
                <p className='text-xs text-muted-foreground line-clamp-2 mt-0.5'>{notification.message}</p>
            </div>
        </div>
    );
}

/**
 * Un lien absolu (ex: fichier signé côté backend) doit être ouvert en
 * navigation normale, pas interprété comme une route interne du SPA.
 */
function ouvrirLien(lien, navigate) {
    if (!lien) return;
    if (/^https?:\/\//.test(lien)) {
        window.location.href = lien;
    } else {
        navigate(lien);
    }
}

function NotificationBell() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const rootRef = useRef(null);
    const navigate = useNavigate();
    const previousUnreadCount = useRef(null);
    // Le listener temps réel est monté une seule fois (voir plus bas) : il a besoin
    // de la valeur à jour de `open` sans redevenir une fonction périmée à chaque
    // ouverture/fermeture du panneau, d'où la ref plutôt qu'une simple closure.
    const openRef = useRef(open);
    useEffect(() => { openRef.current = open; }, [open]);

    // Notifications système (Web Push, voir pushNotifications.js) : le bouton
    // "Activer" ne s'affiche que si le navigateur les supporte et que la
    // permission n'a encore jamais été tranchée (accordée ou refusée) —
    // demander la permission exige un geste utilisateur, donc pas d'auto-popup.
    const [permissionPush, setPermissionPush] = useState(() => (pushSupporte() ? Notification.permission : 'unsupported'));
    const [activationEnCours, setActivationEnCours] = useState(false);

    function activerNotificationsSysteme() {
        setActivationEnCours(true);
        demanderEtSabonner()
            .then(({ ok, raison }) => {
                setPermissionPush(pushSupporte() ? Notification.permission : 'unsupported');
                if (ok) {
                    toast.success(t('notifications.activees'));
                    return;
                }
                // Un échec silencieux ("j'ai cliqué et il ne s'est rien passé")
                // est le pire cas — toujours donner un retour, même vague.
                switch (raison) {
                    case 'permission':
                        toast.error(t('notifications.refusees'));
                        break;
                    case 'reseau':
                        toast.error(t('notifications.reseauInaccessible'));
                        break;
                    case 'non_supporte':
                        toast.error(t('notifications.nonSupporte'));
                        break;
                    default:
                        toast.error(t('notifications.activationEchouee'));
                }
            })
            .finally(() => setActivationEnCours(false));
    }

    const fetchUnreadCount = useCallback(() => {
        getUnreadNotificationsCount()
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (!data) return;
                // Ne joue le son que si le compteur augmente après un premier chargement
                // (évite de sonner à l'ouverture de la page ou quand on lit des notifs).
                if (previousUnreadCount.current !== null && data.count > previousUnreadCount.current) {
                    playNotificationSound();
                }
                previousUnreadCount.current = data.count;
                setUnreadCount(data.count);
            })
            .catch(() => {});
    }, []);

    const fetchNotifications = useCallback(() => {
        setLoading(true);
        getNotifications(20)
            .then(res => res.ok ? res.json() : [])
            .then(data => setNotifications(Array.isArray(data) ? data : []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchUnreadCount();
        // Filet de sécurité si le WebSocket est coupé (réseau, Reverb hors ligne...) —
        // la diffusion en temps réel ci-dessous reste la voie normale.
        const interval = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    // Badge sur l'icône de l'onglet (façon Gmail/réseaux sociaux) — seul repère
    // fiable pour quelqu'un qui a plusieurs onglets ouverts et le son coupé.
    // Un seul point de mise à jour plutôt qu'un appel dans chaque setter
    // d'unreadCount (fetch initial, WebSocket, lecture, suppression...).
    useEffect(() => {
        updateFaviconBadge(unreadCount);
    }, [unreadCount]);

    // Diffusion en temps réel : dès qu'une notification (partage, validation,
    // export prêt, délai dépassé...) est créée côté serveur pour cet utilisateur,
    // elle arrive ici instantanément au lieu d'attendre jusqu'à 30s de polling.
    useEffect(() => {
        const user = JSON.parse(sessionStorage.getItem('user') || '{}');
        if (!user?.id) return;

        const channel = echo.private(`App.Models.Utilisateurs.${user.id}`);
        channel.notification((notification) => {
            playNotificationSound();
            setUnreadCount((prev) => {
                const next = prev + 1;
                previousUnreadCount.current = next;
                return next;
            });
            if (openRef.current) fetchNotifications();

            // Popup visible à l'arrivée, en plus du son et du compteur — sans ça,
            // rien ne signale qu'une notification vient d'arriver tant qu'on n'ouvre
            // pas la cloche soi-même.
            toast(
                ({ closeToast }) => (
                    <ContenuToastNotification
                        notification={notification}
                        onOuvrir={() => ouvrirLien(notification.lien, navigate)}
                        closeToast={closeToast}
                    />
                ),
                { autoClose: 6000, closeButton: ({ closeToast }) => <button onClick={closeToast} className='self-start text-muted-foreground hover:text-foreground'><LuX size={14} /></button> }
            );
        });

        return () => {
            echo.leave(`App.Models.Utilisateurs.${user.id}`);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (open) fetchNotifications();
    }, [open, fetchNotifications]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    function handleItemClick(notification) {
        if (!notification.read_at) {
            setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n));
            setUnreadCount(prev => {
                const next = Math.max(0, prev - 1);
                previousUnreadCount.current = next;
                return next;
            });
            markNotificationAsRead(notification.id).catch(() => {});
        }
        setOpen(false);
        ouvrirLien(notification.data?.lien, navigate);
    }

    function handleDelete(notification) {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        if (!notification.read_at) {
            setUnreadCount(prev => {
                const next = Math.max(0, prev - 1);
                previousUnreadCount.current = next;
                return next;
            });
        }
        return deleteNotification(notification.id).catch(() => {});
    }

    function handleMarkAllRead() {
        setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
        setUnreadCount(0);
        previousUnreadCount.current = 0;
        markAllNotificationsAsRead().catch(() => {});
    }

    return (
        <div className='relative' ref={rootRef}>
            <button
                className='relative text-muted-foreground hover:text-foreground transition-colors'
                onClick={() => setOpen(o => !o)}
            >
                <LuBell size={19} />
                {unreadCount > 0 && (
                    <span className='absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-semibold leading-none'>
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className='fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 top-16 sm:top-auto mt-0 sm:mt-2 w-auto sm:w-80 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-xl shadow-lg z-50 overflow-hidden'>
                    <div className='flex items-center justify-between px-4 py-3 border-b border-border'>
                        <span className='font-semibold text-sm text-foreground'>{t('notifications.titre')}</span>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className='flex items-center gap-1 text-xs text-primary hover:underline'
                            >
                                <LuCheck size={13} /> {t('notifications.toutMarquerLu')}
                            </button>
                        )}
                    </div>

                    {permissionPush === 'default' && (
                        <div className='flex items-center gap-3 px-4 py-3 bg-primary/5 border-b border-border'>
                            <span className='flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary shrink-0'>
                                <LuBellRing size={15} />
                            </span>
                            <div className='flex-1 min-w-0'>
                                <p className='text-xs text-foreground font-medium'>{t('notifications.neRatezPlusRien')}</p>
                                <p className='text-[11px] text-muted-foreground'>{t('notifications.recevezAlerte')}</p>
                            </div>
                            <button
                                onClick={activerNotificationsSysteme}
                                disabled={activationEnCours}
                                className='shrink-0 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50 transition-colors'
                            >
                                {activationEnCours ? t('notifications.activationEnCours') : t('notifications.activer')}
                            </button>
                        </div>
                    )}

                    {permissionPush === 'denied' && (
                        <div className='flex items-start gap-3 px-4 py-3 bg-destructive/5 border-b border-border'>
                            <span className='flex items-center justify-center w-8 h-8 rounded-full bg-destructive/10 text-destructive shrink-0'>
                                <LuBellRing size={15} />
                            </span>
                            <div className='flex-1 min-w-0'>
                                <p className='text-xs text-foreground font-medium'>{t('notifications.bloquees')}</p>
                                <p className='text-[11px] text-muted-foreground'>{t('notifications.autoriserDepuisReglages')}</p>
                            </div>
                        </div>
                    )}

                    {/* Sur mobile, cause la plus fréquente : iOS Safari n'expose l'API
                        Push qu'aux sites ajoutés à l'écran d'accueil (PWA) — sans ça, le
                        bouton "Activer" ne peut tout simplement pas exister, ce qui,
                        vu de l'utilisateur, ressemble à une fonctionnalité manquante. */}
                    {permissionPush === 'unsupported' && (
                        <div className='flex items-start gap-3 px-4 py-3 bg-muted/40 border-b border-border'>
                            <span className='flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground shrink-0'>
                                <LuBellRing size={15} />
                            </span>
                            <div className='flex-1 min-w-0'>
                                <p className='text-xs text-foreground font-medium'>{t('notifications.indisponibles')}</p>
                                <p className='text-[11px] text-muted-foreground'>
                                    {estIOSSafariHorsAccueil()
                                        ? t('notifications.iosAjouterEcranAccueil')
                                        : t('notifications.nonSupporte')}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className='max-h-96 overflow-y-auto'>
                        {loading && (
                            <div className='py-8 text-center text-sm text-muted-foreground'>{t('openFolder.chargement')}</div>
                        )}
                        {!loading && notifications.length === 0 && (
                            <div className='flex flex-col items-center gap-2 py-10 text-muted-foreground'>
                                <LuInbox size={28} />
                                <span className='text-sm'>{t('notifications.aucuneNotification')}</span>
                            </div>
                        )}
                        {!loading && notifications.map(notification => {
                            const visual = TYPE_VISUAL[notification.data?.type] || TYPE_VISUAL.statut;
                            const Icon = visual.icon;
                            const isUnread = !notification.read_at;
                            return (
                                <SwipeToDelete key={notification.id} onDelete={() => handleDelete(notification)} className='rounded-none border-b border-border/60 last:border-0'>
                                    <button
                                        onClick={() => handleItemClick(notification)}
                                        className={`flex w-full gap-3 px-4 py-3 text-left hover:bg-muted/60 transition-colors ${isUnread ? 'bg-primary/5' : ''}`}
                                    >
                                        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${visual.tint}`}>
                                            <Icon size={16} />
                                        </div>
                                        <div className='flex-1 min-w-0'>
                                            <p className='text-sm font-medium text-foreground truncate'>{notification.data?.titre}</p>
                                            <p className='text-xs text-muted-foreground line-clamp-2'>{notification.data?.message}</p>
                                            <p className='text-[11px] text-muted-foreground/70 mt-1'>{timeAgo(notification.created_at)}</p>
                                        </div>
                                        {isUnread && <span className='flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-1.5' />}
                                    </button>
                                </SwipeToDelete>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationBell;
